import React, {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
} from 'react'
import TuneIcon from '@mui/icons-material/Tune'
import AddBox from '@mui/icons-material/AddBox'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import Check from '@mui/icons-material/Check'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Clear from '@mui/icons-material/Clear'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import Edit from '@mui/icons-material/Edit'
import FilterList from '@mui/icons-material/FilterList'
import FirstPage from '@mui/icons-material/FirstPage'
import LastPage from '@mui/icons-material/LastPage'
import Remove from '@mui/icons-material/Remove'
import SaveAlt from '@mui/icons-material/SaveAlt'
import Search from '@mui/icons-material/Search'
import ViewColumn from '@mui/icons-material/ViewColumn'
import Create from '@mui/icons-material/Create'
import CheckCircle from '@mui/icons-material/CheckCircle'
import AddCircle from '@mui/icons-material/AddCircle'
import Assessment from '@mui/icons-material/Assessment'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import NewReleases from '@mui/icons-material/NewReleases'
import Description from '@mui/icons-material/Description'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Palette from '@mui/icons-material/Palette'
import Close from '@mui/icons-material/Close'
import ImageIcon from '@mui/icons-material/Image'
import CachedIcon from '@mui/icons-material/Cached'
import InfoIcon from '@mui/icons-material/Info'
import DateRangeIcon from '@mui/icons-material/DateRange'
import RoomIcon from '@mui/icons-material/Room'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import GroupIcon from '@mui/icons-material/Group'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import WatchLaterIcon from '@mui/icons-material/WatchLater'
import SendIcon from '@mui/icons-material/Send'
import DonutLargeIcon from '@mui/icons-material/DonutLarge'
import CommentIcon from '@mui/icons-material/Comment'
import ForestIcon from '@mui/icons-material/Forest'
import GavelIcon from '@mui/icons-material/Gavel'
import DownloadIcon from '@mui/icons-material/Download'
import EmailIcon from '@mui/icons-material/Email'
import HowToRegIcon from '@mui/icons-material/HowToReg'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import makeStyles from '@mui/styles/makeStyles'
import WorkIcon from '@mui/icons-material/Work'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'

import { SvgIconComponent } from '@material-table/core'
import { useStore } from '@shared/hooks/use-store.hook'
import { Actions } from '@shared/lib/reducer'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import { SvgIconTypeMap } from '@mui/material'
import { IndeterminateCheckBox } from '@mui/icons-material'

type IconComponentType =
  | OverridableComponent<SvgIconTypeMap<{}, 'svg'>>
  | SvgIconComponent

export function forwardedIconFactory(IconComponent: IconComponentType) {
  return forwardRef<SVGSVGElement>((props, ref) => (
    // @ts-ignore
    <IconComponent {...props} ref={ref as any} />
  ))
}

export type ForwardedIconComponent = ForwardRefExoticComponent<
  RefAttributes<SVGSVGElement>
>

const DetailPanel = () => {
  const { state, dispatch } = useStore()
  const classes = useStyles()

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    const parent = e.currentTarget.parentNode?.parentNode?.parentNode
    if (parent) {
      const rowGroup = parent.childNodes[0].childNodes[2]
      if (rowGroup?.hasChildNodes()) {
        const textContainer = rowGroup.childNodes[1]
        if (textContainer) {
          const rowText = textContainer.textContent
          if (!state.expandedValues.includes(rowText)) {
            return dispatch({
              type: Actions.ADD_EXPANDED_VALUE,
              payload: rowText,
            })
          }

          return dispatch({
            type: Actions.REMOVE_EXPANDED_VALUE,
            payload: rowText,
          })
        }
      }
    }
  }

  return (
    <div onClick={handleClick} className={classes.container}>
      <ChevronRight />
    </div>
  )
}

export const useStyles = makeStyles({
  container: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
})

export const originalIcons = {
  Add: AddBox,
  AddCircle,
  Assessment,
  Check,
  CheckCircle: CheckCircle,
  Clear,
  Close,
  Create,
  Delete: DeleteOutline,
  Description,
  DetailPanel,
  Edit,
  Error: ErrorOutline,
  Export: SaveAlt,
  Filter: FilterList,
  FirstPage,
  ImageIcon,
  InfoIcon,
  LastPage,
  NewReleases,
  NextPage: ChevronRight,
  Palette,
  PreviousPage: ChevronLeft,
  ResetSearch: Clear,
  Search,
  CachedIcon,
  SortArrow: ArrowDownward,
  ThirdStateCheck: Remove,
  ViewColumn,
  Visibility,
  VisibilityOff,
  Calendar: DateRangeIcon,
  Pin: RoomIcon,
  Expand: ExpandMoreIcon,
  Group: GroupIcon,
  Tune: TuneIcon,
  Paper: InsertDriveFileIcon,
  PlayArrow: PlayArrowIcon,
  Watch: WatchLaterIcon,
  Send: SendIcon,
  Chart: DonutLargeIcon,
  Comment: CommentIcon,
  Forest: ForestIcon,
  Gavel: GavelIcon,
  Download: DownloadIcon,
  Indeterminate: IndeterminateCheckBox,
  Mail: EmailIcon,
  HowToReg: HowToRegIcon,
  PersonOff: PersonOffIcon,
  TaskAlt: TaskAltIcon,
  Work: WorkIcon,
  PhotoCamera: PhotoCameraIcon,
}

export const icons = Object.entries(originalIcons).reduce(
  (memo: { [key: string]: ForwardedIconComponent }, [key, Component]) => {
    memo[key] = forwardedIconFactory(Component)

    return memo
  },
  {}
)